import {
  findChildOptionGroup,
  getRolledUpParentPriceInCents,
  shouldRollChildPriceIntoParent,
} from "src/pages/configurator/Configurator.utils";
import {
  buildSummaryListEffectiveLineItems,
  filterSelectedOptionsBySteps,
  filterSummaryEffectiveOptionGroups,
  getInteriorPersonalizationGroupsBySubStep,
  getSummaryGroupsEffectivePriceInCents,
  getSummaryPlanDescription,
} from "src/utils/summary.utils";
import { Css } from "~generated/css";
import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";
import { currencyFormatter, getProgramDataStrings } from "~utils";
import { getAduGroupCategory, getAduGroupLabel } from "../../configurator/stepTemplates/Adu.step";
import { SummaryPrintConfiguration_CheckoutConfigFragment } from "../SummaryPrint.page.generated";
import { PDFPage } from "./PDFPage";
import { PrintHeader } from "./PrintHeader";
import { PrintListItem } from "./PrintListItem";

type CustomerSummaryReportProps = {
  data: SummaryPrintConfiguration_CheckoutConfigFragment;
  shouldDisplayPricing: boolean;
};

/**
 * Customer-facing summary PDF. Copies Summary.step section-for-section using the SAME summary utils —
 * NOT the internalBuildSummaryListEffectiveLineItems, so the PDF and screen can't drift on
 * what's shown.
 */
export function CustomerSummaryReport({ data, shouldDisplayPricing }: CustomerSummaryReportProps) {
  const { name, status, createdAt, reservedAt, expiresAt, customerEmail, totalPriceInCents } = data;
  const hasAdu = !!data.aduProductOffering;

  return (
    <PDFPage>
      <div css={Css.relative.h100.w100.bgWhite.$}>
        <div css={Css.df.ma.fdc.gap2.p1.pb4.asc.maxContainer.$}>
          <PrintHeader
            status={status}
            expiredAt={expiresAt}
            createdAt={createdAt}
            reservedAt={reservedAt}
            configurationName={name}
            customerEmail={customerEmail}
            checkoutConfigId={data.id}
            communityId={data.community.id}
          />
          {/* When ADU exists, label + price the main house section */}
          {hasAdu && (
            <div css={Css.df.jcsb.aic.t18.ptPx(12).$}>
              <div>Main House</div>
              {shouldDisplayPricing && <div>{currencyFormatter(data.mhTotalPriceInCents)}</div>}
            </div>
          )}
          <PlanAndExteriorPDFCard data={data} shouldDisplayPricing={shouldDisplayPricing} />
          <InteriorPersonalizationPDFCard data={data} shouldDisplayPricing={shouldDisplayPricing} />
          <AddOnsPDFCard data={data} shouldDisplayPricing={shouldDisplayPricing} />
          {hasAdu && (
            <div css={Css.df.jcsb.aic.t18.ptPx(12).$}>
              <div>ADU</div>
              {shouldDisplayPricing && <div>{currencyFormatter(data.aduTotalPriceInCents)}</div>}
            </div>
          )}
          <AduPDFCard data={data} shouldDisplayPricing={shouldDisplayPricing} />
          <div css={Css.add({ breakInside: "avoid-page" }).$}>
            {/* Estimated Total Cost */}
            {shouldDisplayPricing && (
              <div css={Css.df.fdc.aife.ptPx(12).add({ breakInside: "avoid-page" }).$}>
                <div css={Css.df.colGap1.$}>
                  <h6 css={Css.t18.fsPx(20).fw500.lhPx(32).gap1.$}>
                    <sup>*</sup>Est. Total Cost
                  </h6>
                  <h6 css={Css.t18.fsPx(20).fw500.lhPx(32).gap1.$}>{currencyFormatter(totalPriceInCents)}</h6>
                </div>
              </div>
            )}
            {/* Disclaimer */}
            <div id="disclaimer" css={Css.t12.pt2.tac.gray700.$}>
              <sup>*</sup>Estimated total cost includes site-specific conditions that may vary based on lot conditions.
              All options and prices will be confirmed by a Homebound representative prior to final signature.
            </div>
          </div>
        </div>
      </div>
    </PDFPage>
  );
}

type SectionProps = {
  data: SummaryPrintConfiguration_CheckoutConfigFragment;
  shouldDisplayPricing: boolean;
};

/** Plan & Exterior — mirrors SummaryCards' ExteriorCard (Floor Plan, Exterior, Floor Plan Options). */
function PlanAndExteriorPDFCard({ data, shouldDisplayPricing }: SectionProps) {
  const { optionGroups } = data;
  const { planName, planProgramData } = getSummaryPlanDescription(data);

  const exteriorOptions = filterSelectedOptionsBySteps(optionGroups, [
    CheckoutConfigOptionGroupStep.ExteriorElevation,
    CheckoutConfigOptionGroupStep.ExteriorDesignPackage,
  ]);

  // Note: CheckoutConfigOptionGroupStep.OptionsStructural is not a required OG
  const floorPlanOptionGroups = filterSummaryEffectiveOptionGroups(optionGroups, [
    CheckoutConfigOptionGroupStep.OptionsStructural,
  ]);

  return (
    <div css={Css.df.fdc.$}>
      <PrintListItem
        title="Floor Plan"
        shouldDisplayPricing={shouldDisplayPricing}
        priceInCents={data.mhSalesPriceInCents}
        items={[{ title: planName }, { title: planProgramData }]}
      />
      <PrintListItem
        title="Exterior"
        shouldDisplayPricing={shouldDisplayPricing}
        hideOptionCodes
        priceInCents={exteriorOptions.sum((cco) => cco.totalPriceInCents)}
        items={exteriorOptions.map((cco) => ({ title: cco.option.name, priceInCents: cco.totalPriceInCents }))}
      />
      <PrintListItem
        title="Floor Plan Options"
        shouldDisplayPricing={shouldDisplayPricing}
        hideOptionCodes
        priceInCents={getSummaryGroupsEffectivePriceInCents(floorPlanOptionGroups)}
        items={buildSummaryListEffectiveLineItems(floorPlanOptionGroups, false, optionGroups)}
      />
    </div>
  );
}

/** Interior personalization by sub-step/room — mirrors SummaryCards' InteriorPersonalizationCard. */
function InteriorPersonalizationPDFCard({ data, shouldDisplayPricing }: SectionProps) {
  const { optionGroups } = data;
  const interiorSubSteps = data.interiorSubSteps ?? [];
  const personalizationGroupsBySubStep = getInteriorPersonalizationGroupsBySubStep(optionGroups);

  return (
    <div css={Css.df.fdc.$}>
      {interiorSubSteps.map(({ displayName, stepNumber }) => {
        const groups = personalizationGroupsBySubStep[stepNumber];
        if (!groups?.length) return null;
        return (
          <div key={stepNumber} css={Css.df.fdc.add({ breakInside: "avoid-page" }).$}>
            <div css={Css.t16.gray900.fw600.ptPx(12).$}>{displayName}</div>
            {groups.map((optionGroup) => {
              // Mirror the screen: the swatch child's price rolls into this parent's total, but we surface the
              // child's selected option as its own nested line (own price) so the user sees which swatch they picked.
              const childGroup = findChildOptionGroup(optionGroup, optionGroups);
              const rollsUpChild =
                !!childGroup && shouldRollChildPriceIntoParent(childGroup, childGroup.selectedOption);
              const priceInCents = getRolledUpParentPriceInCents(
                optionGroup.selectedOption,
                childGroup,
                childGroup?.selectedOption,
              );
              return (
                <PrintListItem
                  key={optionGroup.id}
                  title={optionGroup.group.name}
                  shouldDisplayPricing={shouldDisplayPricing}
                  priceInCents={priceInCents}
                  items={[
                    {
                      title: optionGroup.selectedOption!.option.name,
                      priceInCents: optionGroup.selectedOption!.totalPriceInCents,
                    },
                    ...(rollsUpChild
                      ? [
                          {
                            title: childGroup!.selectedOption!.option.name,
                            priceInCents: childGroup!.selectedOption!.totalPriceInCents,
                          },
                        ]
                      : []),
                  ]}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/** Add-Ons — mirrors SummaryCards' AddOnsCard. Gated when empty (no empty section on the customer doc). */
function AddOnsPDFCard({ data, shouldDisplayPricing }: SectionProps) {
  const { optionGroups } = data;
  const selectedAddOns = filterSummaryEffectiveOptionGroups(optionGroups, [CheckoutConfigOptionGroupStep.OptionsAddon]);
  if (selectedAddOns.isEmpty) return null;

  return (
    <div css={Css.df.fdc.add({ breakInside: "avoid-page" }).$}>
      <PrintListItem
        title="Add-Ons"
        shouldDisplayPricing={shouldDisplayPricing}
        hideOptionCodes
        priceInCents={getSummaryGroupsEffectivePriceInCents(selectedAddOns)}
        items={buildSummaryListEffectiveLineItems(selectedAddOns, true, optionGroups)}
      />
    </div>
  );
}

/** ADU — mirrors SummaryCards' AduCard (legacy flat list, or new floor plan + grouped options). */
function AduPDFCard({ data, shouldDisplayPricing }: SectionProps) {
  const { optionGroups } = data;
  const selectedAduOptions = filterSummaryEffectiveOptionGroups(optionGroups, [CheckoutConfigOptionGroupStep.Adu]);

  // Legacy ADU — flat list of selections
  if (!data.aduProductOffering) {
    if (selectedAduOptions.isEmpty) return null;
    return (
      <div css={Css.df.fdc.add({ breakInside: "avoid-page" }).$}>
        <PrintListItem
          title="ADU Selections"
          shouldDisplayPricing={shouldDisplayPricing}
          hideOptionCodes
          priceInCents={getSummaryGroupsEffectivePriceInCents(selectedAduOptions)}
          items={buildSummaryListEffectiveLineItems(selectedAduOptions, true, optionGroups)}
        />
      </div>
    );
  }

  // New ADU flow — show floor plan, then grouped options
  const { bedrooms, bathrooms, sqft } = getProgramDataStrings(data.aduProgramData);
  const { name } = data.aduProductOffering;
  const aduSalesPrice = data.aduSalesPriceInCents;

  const aduOptionsByGroup = selectedAduOptions.groupBy((ccog) =>
    getAduGroupLabel(getAduGroupCategory(ccog.group.group.type), true),
  );

  return (
    <div css={Css.df.fdc.add({ breakInside: "avoid-page" }).$}>
      <PrintListItem
        title="Floor Plan"
        shouldDisplayPricing={shouldDisplayPricing}
        priceInCents={aduSalesPrice}
        items={[{ title: name, priceInCents: aduSalesPrice }, { title: `${bedrooms}, ${bathrooms} & ${sqft}` }]}
      />
      {Object.entries(aduOptionsByGroup).map(([groupName, groups]) => (
        <PrintListItem
          key={groupName}
          title={groupName}
          shouldDisplayPricing={shouldDisplayPricing}
          hideOptionCodes
          priceInCents={getSummaryGroupsEffectivePriceInCents(groups)}
          items={buildSummaryListEffectiveLineItems(groups, true, optionGroups)}
        />
      ))}
    </div>
  );
}
